import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

const DATA_RETENTION_API = '/v1/data-retention';

function getCompanyID(): string {
    return (window as any).companyID ? (window as any).companyID : localStorage.getItem('companyID') || '';
}

export namespace DataRetentionSchedule {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface DataTypeHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }

    export interface DataTypeEntity {
        dataTypeID: number;
        dataTypeName: string;
        dataTypeJustification: string;
        dataRetentionPeriod: {
            min: number;
            max: number;
        };
        dataTypeModeOfCollection: string;
        isPersonalInformation: boolean;
        dataTypeMetadata: {
            addedBy: string;
            dateAdded: Date;
            dateLastUpdated: Date;
        };
        deleted: boolean;
        status?: 'active' | 'inactive';
    }

    export interface DataRetentionScheduleResponse {
        statusCode?: number;
        message?: string;
        dataTypes?: DataTypeEntity[];
        entry?: DataTypeEntity;
        error?: string | null;
    }

    export class DataTypeInstance {
        private data: DataRetentionSchedule.DataTypeEntity;

        constructor(data: DataRetentionSchedule.DataTypeEntity) {
            this.data = data;
        }

        public get entity(): DataRetentionSchedule.DataTypeEntity {
            return this.data;
        }

        public async edit(updates: Partial<DataRetentionSchedule.DataTypeEntity>): Promise<DataRetentionSchedule.DataRetentionScheduleResponse | void> {
            return DataRetentionSchedule.DataType.Edit(this.data.dataTypeID, updates);
        }
    }

    export class DataType {
        static async Add(
            id: number,
            dataTypeData: Partial<DataTypeEntity>,
            callback?: (res: DataRetentionScheduleResponse) => any
        ): Promise<DataRetentionScheduleResponse | void> {
            const companyID = getCompanyID();
            console.log("Calling Thalassa API Add", { companyID, ...dataTypeData });

            const res = await deiterateAPI.POST(`${DATA_RETENTION_API}/types/${id}`, {
                mode: 'cors',
                backend: 'thalassa',
                headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID },
                body: JSON.stringify(dataTypeData)
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        static async Delete(
            id: number,
            callback?: (res: DataRetentionScheduleResponse) => any
        ): Promise<DataRetentionScheduleResponse | void> {
            const companyID = getCompanyID();
            console.log("Calling Thalassa API Delete", { companyID });

            const res = await deiterateAPI.DELETE(`${DATA_RETENTION_API}/types/${id}`, {
                mode: 'cors',
                backend: 'thalassa',
                headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
            });

            console.log('API Response', res);
            if (callback) return callback(res);
            return res;
        }

        static async Edit(
            id: number,
            dataTypeData: Partial<DataTypeEntity>,
            callback?: (res: DataRetentionScheduleResponse) => any
        ): Promise<DataRetentionScheduleResponse | void> {
            const companyID = getCompanyID();
            console.log("Calling Thalassa API Edit", { companyID, ...dataTypeData });

            const res = await deiterateAPI.PUT(`${DATA_RETENTION_API}/types/${id}`, {
                mode: 'cors',
                backend: 'thalassa',
                headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID },
                body: JSON.stringify(dataTypeData)
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        static async Get(): Promise<DataRetentionSchedule.DataTypeEntity[] | undefined>;
        static async Get(id: number): Promise<DataTypeInstance | undefined>;
        static async Get(id?: number): Promise<DataRetentionSchedule.DataTypeEntity[] | DataTypeInstance | undefined> {
            const companyID = getCompanyID();
            
            if (typeof id === 'undefined' || id === -1) {
                // Fetch all data types
                const res = await deiterateAPI.GET(`${DATA_RETENTION_API}/types`, {
                    mode: 'cors',
                    backend: 'thalassa',
                    headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
                });
                return res.dataTypes;
            } else {
                try {
                    // Fetch single data type
                    const res = await deiterateAPI.GET(`${DATA_RETENTION_API}/types/${id}`, {
                        mode: 'cors',
                        backend: 'thalassa',
                        headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
                    });

                    if (!res.entry) {
                        return undefined;
                    }

                    return new DataTypeInstance(res.entry);
                } catch (error) {
                    console.error('Error fetching data type:', error);
                    return undefined;
                }
            }
        }
    }
}
